<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-content">
      <div class="modal-header">
        <h3>{{ $t('legacy.Загрузить обложку проекта') }}</h3>
        <button @click="$emit('close')" class="btn-close" :aria-label="$t('common.close')">×</button>
      </div>

      <div class="upload-section" v-if="!uploadedImage">
        <input 
          ref="fileInput"
          type="file" 
          accept="image/*"
          @change="handleFileUpload"
          style="display: none"
        />
        <div class="upload-placeholder" @click="$refs.fileInput.click()">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <p>{{ $t('legacy.Нажмите для загрузки изображения') }}</p>
          <span class="hint">{{ $t('Изображение будет оптимизировано (макс. 600px по ширине)') }}</span>
        </div>
      </div>
      
      <div v-else class="preview-section">
        <div class="image-preview-container">
          <img :src="uploadedImage" :alt="$t('legacy.Превью обложки')" class="image-preview" />
        </div>
        
        <div v-if="isSaving" class="upload-progress">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
          </div>
          <span class="progress-label">{{ uploadProgress }}%</span>
        </div>
        
        <div class="preview-actions">
          <button @click="resetUpload" class="btn-secondary" :disabled="isSaving">{{ $t('common.cancel') }}</button>
          <button @click="saveImage" class="btn-primary" :disabled="isSaving">
            {{ isSaving ? $t('Сохранение...') : $t('legacy.Сохранить обложку') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { updateProjectCover } from '@/api/projects'
import { useToast } from 'vue-toastification'

export default {
  name: 'AppCoverModal',
  props: {
    projectId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      uploadedImage: null,
      isSaving: false,
      uploadProgress: 0,
      toast: useToast()
    }
  },
  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0]
      if (file) {
        if (file.size > 10 * 1024 * 1024) {
          this.toast.error(this.$t('Размер файла не должен превышать 10 МБ'))
          return
        }
        const reader = new FileReader()
        reader.onload = (e) => {
          this.optimizeImage(e.target.result)
        }
        reader.readAsDataURL(file)
      }
    },

    optimizeImage(dataUrl) {
      const img = new Image()
      img.onload = () => {
        const maxWidth = 600
        let width = img.width
        let height = img.height
        
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width)
          width = maxWidth
        }
        
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        
        this.uploadedImage = canvas.toDataURL('image/jpeg', 0.85)
      }
      img.onerror = () => {
        this.toast.error(this.$t('legacy.Не удалось загрузить изображение'))
      }
      img.src = dataUrl
    },

    resetUpload() {
      this.uploadedImage = null
      this.uploadProgress = 0
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    async saveImage() {
      if (!this.uploadedImage) {
        this.toast.error(this.$t('legacy.Изображение не загружено'))
        return
      }

      this.isSaving = true
      this.uploadProgress = 0

      try {
        const response = await updateProjectCover(
          this.projectId, 
          this.uploadedImage,
          (progressEvent) => {
            if (progressEvent.total) {
              this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            }
          }
        )
        
        if (response.success) {
          this.toast.success(this.$t('legacy.Обложка успешно сохранена'))
          this.$emit('save', response.result?.cover || this.uploadedImage)
        } else {
          this.toast.error(this.$t('legacy.Ошибка сохранения обложки'))
        }
      } catch (error) {
        console.error('Ошибка сохранения обложки:', error)
        this.toast.error(this.$t('legacy.Не удалось сохранить обложку'))
      } finally {
        this.isSaving = false
        this.uploadProgress = 0
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/global.scss";

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #eee;

  h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #26374E;
  }
}

.btn-close {
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #999;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
    color: #333;
  }
}

.upload-section {
  padding: 24px;
}

.upload-placeholder {
  border: 2px dashed #ccc;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: #496782;
    background: #f8fafc;
  }

  svg {
    color: #999;
    margin-bottom: 16px;
  }

  p {
    margin: 0 0 8px;
    font-size: 16px;
    color: #333;
  }

  .hint {
    font-size: 13px;
    color: #999;
  }
}

.preview-section {
  padding: 24px;
}

.image-preview-container {
  width: 100%;
  aspect-ratio: 4 / 3;
  background: #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
  justify-content: flex-end;
}

.btn-primary {
  background: #496782;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #3a5268;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
}

.btn-secondary {
  background: #f0f0f0;
  color: #333;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #e0e0e0;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.upload-progress {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #496782, #6b8ba8);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-label {
  font-size: 14px;
  font-weight: 600;
  color: #496782;
  min-width: 45px;
  text-align: right;
}

@media (max-width: 640px) {
  .modal-content {
    max-width: 100%;
    margin: 10px;
  }

  .preview-actions {
    flex-direction: column;
  }
}
</style>
